import { PotDetails, PotSummary } from '../pot/PotDetails';
import React, { useState } from 'react';
import { Button, Collapse, Descriptions, Drawer, Result, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useUserMatches } from './queries';
import { UserMatch } from './types';
import { formatDateTime } from '../../utils/format';

export function GameHistoryTab({ userID }: { userID: string }) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [selected, setSelected] = useState<UserMatch | null>(null);
  const matches = useUserMatches(userID, page, perPage);
  const columns: ColumnsType<UserMatch> = [
    { title: 'Room', dataIndex: 'external_room_id' },
    { title: 'Match', dataIndex: 'match_number', render: (value: number | null) => value ?? '—' },
    { title: 'Pot', render: (_, row) => <PotSummary pot={row.pot} /> },
    { title: 'Rank', dataIndex: 'rank', render: (value: number | null) => value ?? '—' },
    { title: 'Win type', dataIndex: 'win_type', render: (value: string | null) => value ? <Tag>{value}</Tag> : '—' },
    { title: 'Gold change', dataIndex: 'gold_delta', render: (value: number) => <Typography.Text type={value < 0 ? 'danger' : value > 0 ? 'success' : undefined}>{value > 0 ? '+' : ''}{value.toLocaleString()}</Typography.Text> },
    { title: 'EXP gained', dataIndex: 'exp_gained', render: (value: number) => value.toLocaleString() },
    { title: 'Joined', dataIndex: 'joined_at', render: formatDateTime },
    { title: 'Ended', dataIndex: 'end_at', render: formatDateTime },
    { title: 'Actions', render: (_, row) => <Button type="link" onClick={() => setSelected(row)}>View</Button> },
  ];
  return <>
    <div className="flex justify-end mb-4"><Button loading={matches.isFetching} onClick={() => matches.refetch()}>Refresh games</Button></div>
    {matches.error ? <Result status="error" title="Failed to load game history" subTitle={matches.error instanceof Error ? matches.error.message : 'Request failed'} extra={<Button onClick={() => matches.refetch()}>Retry</Button>} /> :
      <Table rowKey="id" columns={columns} dataSource={matches.data?.data || []} loading={matches.isLoading} scroll={{ x: 1100 }} pagination={{ current: page, pageSize: perPage, total: matches.data?.meta.total || 0, showSizeChanger: true, pageSizeOptions: [20, 50, 100], disabled: matches.isPlaceholderData, showTotal: total => `${total} games`, onChange: (nextPage, size) => { setPage(size !== perPage ? 1 : nextPage); setPerPage(size); } }} />}
    <Drawer title="Game details" width={760} open={!!selected} onClose={() => setSelected(null)} destroyOnClose>
      {selected && <>
        <Descriptions bordered column={1} items={[
          { key: 'room', label: 'Room', children: selected.external_room_id },
          { key: 'match', label: 'Match number', children: selected.match_number ?? '—' },
          { key: 'rank', label: 'Rank', children: selected.rank ?? '—' },
          { key: 'type', label: 'Win type', children: selected.win_type || '—' },
          { key: 'gold', label: 'Gold change', children: selected.gold_delta.toLocaleString() },
          { key: 'exp', label: 'EXP gained', children: selected.exp_gained.toLocaleString() },
          { key: 'joined', label: 'Joined', children: formatDateTime(selected.joined_at) },
          { key: 'ended', label: 'Ended', children: formatDateTime(selected.end_at) },
        ]} />
        <PotDetails pot={selected.pot} />
        <Collapse className="mt-4" items={[
          { key: 'score', label: 'Score details', children: <pre className="whitespace-pre-wrap break-words">{JSON.stringify(selected.score_detail, null, 2) || '—'}</pre> },
          { key: 'settlement', label: 'Settlement details', children: <pre className="whitespace-pre-wrap break-words">{JSON.stringify(selected.settlement_detail, null, 2) || '—'}</pre> },
        ]} />
      </>}
    </Drawer>
  </>;
}
